import React, { Fragment, useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import { useDispatch } from "react-redux";
import { getPromoCodes, getPromoUsers } from "../../store/actions/PromoCodeActions";
import { Spinner, Card, Form } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";

const PromoUsersList = () => {
  const { changeBackground } = useContext(ThemeContext);
  const dispatch = useDispatch();

  const [promoCodes, setPromoCodes] = useState([]);
  const [selectedPromoId, setSelectedPromoId] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("promo_id") || "";
  });
  const [promoUsers, setPromoUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCodesLoading, setIsCodesLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const columns = [
    "#",
    "User Name",
    "Email",
    "Promo Code",
    "Service Type",
    "Billing Type",
    "Sent Status",
    "Sent At",
    "Used Status",
    "Used At",
  ];

  // Set theme background to light on mount
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    fetchPromoCodesList();
  }, []);

  const fetchPromoCodesList = async () => {
    setIsCodesLoading(true);
    try {
      const res = await dispatch(getPromoCodes());
      let list = [];
      if (Array.isArray(res?.payload)) {
        list = res.payload;
      } else if (Array.isArray(res?.payload?.results)) {
        list = res.payload.results;
      } else if (Array.isArray(res?.payload?.data)) {
        list = res.payload.data;
      }
      setPromoCodes(list);
    } catch (err) {
      console.error("Error fetching promo codes:", err);
    } finally {
      setIsCodesLoading(false);
    }
  };

  const fetchPromoUsersList = async () => {
    setIsLoading(true);
    try {
      const res = await dispatch(
        getPromoUsers({
          promo_id: selectedPromoId || undefined,
          page,
          size: pageSize,
        })
      );

      const payload = res?.payload;
      let userList = [];
      let count = 0;

      if (payload) {
        if (Array.isArray(payload)) {
          userList = payload;
          count = payload.length;
        } else if (Array.isArray(payload.results)) {
          userList = payload.results;
          count = payload.count || payload.results.length;
        } else if (Array.isArray(payload.data)) {
          userList = payload.data;
          count = payload.count || payload.data.length;
        }
      }

      setPromoUsers(userList);
      setTotalCount(count);
    } catch (err) {
      console.error("Error fetching promo users:", err);
      toast.error("Failed to load promo code users!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoUsersList();
  }, [selectedPromoId, page]);

  const handlePromoSelectChange = (e) => {
    setSelectedPromoId(e.target.value);
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <Fragment>
      <div className="col-12">
        <Card>
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <h4 className="card-title mb-0">Promo Code Users</h4>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Form.Select
                value={selectedPromoId}
                onChange={handlePromoSelectChange}
                style={{ width: "240px" }}
                size="sm"
                disabled={isCodesLoading}
              >
                <option value="">All Promo Codes</option>
                {promoCodes.map((promo) => (
                  <option key={promo.id} value={promo.id}>
                    {promo.code}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Card.Header>
          <Card.Body>
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <div className="w-100 table-responsive">
                <div id="example_wrapper" className="dataTables_wrapper">
                  <table id="example3" className="display w-100 dataTable">
                    <thead>
                      <tr role="row">
                        {columns.map((d, i) => (
                          <th key={i}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {promoUsers && promoUsers.length > 0 ? (
                        promoUsers.map((item, index) => (
                          <tr key={item.id || index}>
                            <td style={{ fontSize: "14px" }}>
                              {(page - 1) * pageSize + index + 1}
                            </td>
                            <td style={{ fontWeight: 600, fontSize: "14px" }}>
                              {item.user_name || `User #${item.user_id}`}
                            </td>
                            <td style={{ fontSize: "14px" }}>{item.email || "-"}</td>
                            <td style={{ fontSize: "14px" }}>
                              <span className="badge badge-outline-primary">
                                {item.promo_code || "-"}
                              </span>
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.service_type || "-"}
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.billing_type || "-"}
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.is_sent ? (
                                <span className="badge badge-success light">
                                  Sent
                                </span>
                              ) : (
                                <span className="badge badge-secondary light">
                                  Not Sent
                                </span>
                              )}
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.sent_at
                                ? moment(item.sent_at).format("YYYY-MM-DD HH:mm")
                                : "-"}
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.is_used ? (
                                <span className="badge badge-primary light">
                                  Used
                                </span>
                              ) : (
                                <span className="badge badge-warning light">
                                  Unused
                                </span>
                              )}
                            </td>
                            <td style={{ fontSize: "14px" }}>
                              {item.used_at
                                ? moment(item.used_at).format("YYYY-MM-DD HH:mm")
                                : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4 text-muted">
                            No promo code users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                    <div className="dataTables_info">
                      {totalCount > 0
                        ? `Showing ${(page - 1) * pageSize + 1} to ${Math.min(
                            page * pageSize,
                            totalCount
                          )} of ${totalCount} entries`
                        : "Showing 0 to 0 of 0 entries"}
                    </div>
                    <div className="dataTables_paginate paging_simple_numbers">
                      <button
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        disabled={page <= 1 || isLoading}
                      >
                        Previous
                      </button>
                      <span className="me-2 align-self-center fs-14">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                        disabled={page >= totalPages || isLoading}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Fragment>
  );
};

export default PromoUsersList;
